if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback () {
        this.input = this.querySelector('[data-predictive-search-input]')
        this.results = this.querySelector('[data-predictive-search-results]')
        this.status = this.querySelector('[data-predictive-search-status]')
        this.searchTerm = ''
        this.debounceTimer = null
        this.abortController = null

        if (!this.input || !this.results) return

        this.handleInput = this.handleInput.bind(this)
        this.handleFocus = this.handleFocus.bind(this)
        this.handleKeydown = this.handleKeydown.bind(this)
        this.handleFocusOut = this.handleFocusOut.bind(this)
        this.input.addEventListener('input', this.handleInput)
        this.input.addEventListener('focus', this.handleFocus)
        this.addEventListener('keydown', this.handleKeydown)
        this.addEventListener('focusout', this.handleFocusOut)
      }

      disconnectedCallback () {
        window.clearTimeout(this.debounceTimer)
        this.abortController?.abort()
        this.input?.removeEventListener('input', this.handleInput)
        this.input?.removeEventListener('focus', this.handleFocus)
        this.removeEventListener('keydown', this.handleKeydown)
        this.removeEventListener('focusout', this.handleFocusOut)
      }

      handleInput () {
        window.clearTimeout(this.debounceTimer)
        const term = this.input.value.trim()

        if (term.length < 2) {
          this.close()
          return
        }

        this.debounceTimer = window.setTimeout(() => this.fetchResults(term), 300)
      }

      handleFocus () {
        const term = this.input.value.trim()
        if (term.length < 2) return

        if (term === this.searchTerm && this.results.innerHTML.trim()) {
          this.open()
        } else {
          this.fetchResults(term)
        }
      }

      async fetchResults (term) {
        if (term === this.searchTerm && this.results.innerHTML.trim()) {
          this.open()
          return
        }

        this.abortController?.abort()
        this.abortController = new AbortController()
        const controller = this.abortController
        this.setLoading(true)

        const params = new URLSearchParams({
          q: term,
          section_id: 'predictive-search',
          'resources[type]': 'query,product,collection,page,article',
          'resources[limit]': '4',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,vendor'
        })

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?${params}`, {
            signal: controller.signal
          })
          if (!response.ok) throw new Error('Predictive search could not be loaded')

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html')
          const section = documentFragment.querySelector('#shopify-section-predictive-search')
          this.results.innerHTML = section?.innerHTML || ''
          this.searchTerm = term
          this.open()
        } catch (error) {
          if (error.name !== 'AbortError') this.close()
        } finally {
          if (this.abortController === controller) this.setLoading(false)
        }
      }

      setLoading (isLoading) {
        this.toggleAttribute('data-loading', isLoading)
        this.input.setAttribute('aria-busy', String(isLoading))
        if (isLoading && this.status) this.status.textContent = this.dataset.loadingText
      }

      open () {
        if (!this.results.innerHTML.trim()) return
        this.results.hidden = false
        this.input.setAttribute('aria-expanded', 'true')
        if (this.status) this.status.textContent = this.dataset.resultsText
      }

      close () {
        this.results.hidden = true
        this.input.setAttribute('aria-expanded', 'false')
        this.input.removeAttribute('aria-activedescendant')
        if (this.status) this.status.textContent = ''
      }

      handleFocusOut (event) {
        if (!this.contains(event.relatedTarget)) this.close()
      }

      handleKeydown (event) {
        if (event.key === 'Escape') {
          this.close()
          this.input.focus()
          return
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

        const options = Array.from(this.results.querySelectorAll('[role="option"]'))
        if (!options.length) return

        event.preventDefault()
        const currentIndex = options.indexOf(document.activeElement)
        const nextIndex = event.key === 'ArrowDown'
          ? (currentIndex + 1) % options.length
          : (currentIndex <= 0 ? options.length - 1 : currentIndex - 1)
        options[nextIndex].focus()
      }
    }
  )
}
